import { Injectable, signal, computed } from '@angular/core';
import { PendingByGapAPI, PendingByGapItem, PendingByGapRecord } from '../interface/arrangment-cron-api.interface';

// Analytics Interfaces
export interface RecordsByPeriod {
  period: string;
  periodLabel: string;
  count: number;
  totalAmount: number;
  recurringCount: number;
  partialCount: number;
  avgDaysUntil: number;
  records: PendingByGapRecord[];
}

export interface RecordsByAdmin {
  adminName: string;
  count: number;
  totalScheduledAmount: number;
  recurringCount: number;
  partialCount: number;
  avgRecurringAmount: number;
  contracts: string[];
  records: PendingByGapRecord[];
}

export interface RecordsByStatus {
  status: string;
  count: number;
  totalAmount: number;
  percentage: number;
  records: PendingByGapRecord[];
}

export interface RecordsByPaymentType {
  paymentType: string;
  count: number;
  totalAmount: number;
  avgAmount: number;
  percentage: number;
  records: PendingByGapRecord[];
}

export interface RecordsBySlot {
  slot: string;
  count: number;
  totalAmount: number;
  percentage: number;
  records: PendingByGapRecord[];
}

export interface RecurringAmountRecord {
  arrangementId: number;
  contractCode: string;
  patientName: string;
  recurringAmount: number;
  scheduledAmount: number;
  remainingPayments: number;
  createdBy: string;
  gapRange: string;
  arrangementDate: string;
}

export interface PartialAmountRecord {
  arrangementId: number;
  contractCode: string;
  patientName: string;
  partialAmount: number;
  createdBy: string;
  gapRange: string;
  arrangementDate: string;
  contractStatus: string;
}

export interface TimelineRecord {
  date: string;
  dateLabel: string;
  count: number;
  totalAmount: number;
  gapRange: string;
}

export interface GapDistribution {
  gapRange: string;
  gapOrder: number;
  count: number;
  percentage: number;
  totalAmount: number;
  amountPercentage: number;
  avgDaysUntil: number;
  hasFarFutureAlert: boolean;
}

export interface AdminPerformanceMetrics {
  adminName: string;
  totalArrangements: number;
  avgScheduledAmount: number;
  recurringPercentage: number;
  contractDiversity: number;
  recentActivityScore: number;
}

export interface AnalyticsSummary {
  totalRecords: number;
  totalScheduledAmount: number;
  avgScheduledAmount: number;
  totalRecurring: number;
  totalPartial: number;
  uniqueAdmins: number;
  uniqueContracts: number;
  farFutureCount: number;
  avgDaysUntil: number;
  highestRecurringAmount: number;
  lowestRecurringAmount: number;
}

@Injectable({
  providedIn: 'root'
})
export class PendingAnalyticsService {
  private pendingData = signal<PendingByGapAPI | null>(null);
  private allRecords = signal<(PendingByGapRecord & { gapRange: string })[]>([]);

  /**
   * Gets the appropriate amount based on payment type.
   * If payment_type_name is "Recurring", returns recurring_amount, otherwise returns partial_amount.
   */
  private getAmountByPaymentType(record: PendingByGapRecord): number {
    const paymentType = record['payment_type_name'];
    if (paymentType === 'Recurring') {
      return parseFloat(record['recurring_amount'] || '0');
    } else {
      return parseFloat(record['partial_amount'] || '0');
    }
  }

  // Computed analytics
  analyticsSummary = computed<AnalyticsSummary | null>(() => {
    const data = this.pendingData();
    const records = this.allRecords();
    if (!data || records.length === 0) return null;

    const recurringAmounts = records
      .map(r => parseFloat(r['recurring_amount'] || '0'))
      .filter(a => a > 0);

    const uniqueAdmins = new Set(records.map(r => r['created_by'])).size;
    const uniqueContracts = new Set(records.map(r => r['contract_code'])).size;

    return {
      totalRecords: data.summary.total_pending,
      totalScheduledAmount: data.summary.total_scheduled_amount,
      avgScheduledAmount: data.summary.total_scheduled_amount / (data.summary.total_pending || 1),
      totalRecurring: data.by_gap.reduce((sum, g) => sum + g.recurring_count, 0),
      totalPartial: data.by_gap.reduce((sum, g) => sum + g.partial_count, 0),
      uniqueAdmins,
      uniqueContracts,
      farFutureCount: data.summary.far_future_alert,
      avgDaysUntil: data.by_gap.reduce((sum, g) => sum + g.avg_days_until * g.total_arrangements, 0) / 
                    (data.summary.total_pending || 1),
      highestRecurringAmount: Math.max(...recurringAmounts, 0),
      lowestRecurringAmount: Math.min(...recurringAmounts.filter(a => a > 0), 0)
    };
  });

  // Records by Month (based on arrangement_date)
  recordsByMonth = computed<RecordsByPeriod[]>(() => {
    const records = this.allRecords().filter(r => r['days_until'] <= 90);
    if (records.length === 0) return [];

    const byMonth = new Map<string, { records: (PendingByGapRecord & { gapRange: string })[], totalAmount: number }>();

    records.forEach(record => {
      const date = new Date(record['arrangement_date']);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

      if (!byMonth.has(monthKey)) {
        byMonth.set(monthKey, { records: [], totalAmount: 0 });
      }
      const entry = byMonth.get(monthKey)!;
      entry.records.push(record);
      entry.totalAmount += this.getAmountByPaymentType(record);
    });

    return Array.from(byMonth.entries())
      .map(([period, data]) => {
        const recurringCount = data.records.filter(r => r['payment_type_name'] === 'Recurring').length;
        const avgDaysUntil = data.records.reduce((sum, r) => sum + (r['days_until'] || 0), 0) / data.records.length;
        const date = new Date(period + '-01');
        return {
          period,
          periodLabel: date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
          count: data.records.length,
          totalAmount: data.totalAmount,
          recurringCount,
          partialCount: data.records.length - recurringCount,
          avgDaysUntil,
          records: data.records
        };
      })
      .sort((a, b) => a.period.localeCompare(b.period));
  });

  // Records by Year (based on arrangement_date)
  recordsByYear = computed<RecordsByPeriod[]>(() => {
    const records = this.allRecords().filter(r => r['days_until'] <= 90);
    if (records.length === 0) return [];

    const byYear = new Map<string, { records: (PendingByGapRecord & { gapRange: string })[], totalAmount: number }>();

    records.forEach(record => {
      const date = new Date(record['arrangement_date']);
      const yearKey = String(date.getFullYear());

      if (!byYear.has(yearKey)) {
        byYear.set(yearKey, { records: [], totalAmount: 0 });
      }
      const entry = byYear.get(yearKey)!;
      entry.records.push(record);
      entry.totalAmount += this.getAmountByPaymentType(record);
    });

    return Array.from(byYear.entries())
      .map(([period, data]) => {
        const recurringCount = data.records.filter(r => r['payment_type_name'] === 'Recurring').length;
        const avgDaysUntil = data.records.reduce((sum, r) => sum + (r['days_until'] || 0), 0) / data.records.length;
        return {
          period,
          periodLabel: period,
          count: data.records.length,
          totalAmount: data.totalAmount,
          recurringCount,
          partialCount: data.records.length - recurringCount,
          avgDaysUntil,
          records: data.records
        };
      })
      .sort((a, b) => a.period.localeCompare(b.period));
  });

  // Records by Admin
  recordsByAdmin = computed<RecordsByAdmin[]>(() => {
    const records = this.allRecords();
    if (records.length === 0) return [];

    const byAdmin = new Map<string, PendingByGapRecord[]>();

    records.forEach(record => {
      const admin = record['created_by'] || 'Unknown';
      if (!byAdmin.has(admin)) {
        byAdmin.set(admin, []);
      }
      byAdmin.get(admin)!.push(record);
    });

    return Array.from(byAdmin.entries())
      .map(([adminName, adminRecords]) => {
        const totalScheduledAmount = adminRecords.reduce((sum, r) => 
          sum + this.getAmountByPaymentType(r), 0);
        const recurringRecords = adminRecords.filter(r => r['payment_type_name'] === 'Recurring');
        const recurringAmounts = recurringRecords.map(r => parseFloat(r['recurring_amount'] || '0')).filter(a => a > 0);
        const contracts = [...new Set(adminRecords.map(r => r['contract_code']))];

        return {
          adminName,
          count: adminRecords.length,
          totalScheduledAmount,
          recurringCount: recurringRecords.length,
          partialCount: adminRecords.length - recurringRecords.length,
          avgRecurringAmount: recurringAmounts.length > 0 
            ? recurringAmounts.reduce((a, b) => a + b, 0) / recurringAmounts.length 
            : 0,
          contracts,
          records: adminRecords
        };
      })
      .sort((a, b) => b.count - a.count);
  });

  // Records by Contract Status
  recordsByStatus = computed<RecordsByStatus[]>(() => {
    const records = this.allRecords();
    if (records.length === 0) return [];

    const byStatus = new Map<string, PendingByGapRecord[]>();

    records.forEach(record => {
      const status = record['contract_status'] || 'Unknown';
      if (!byStatus.has(status)) {
        byStatus.set(status, []);
      }
      byStatus.get(status)!.push(record);
    });

    const total = records.length;
    return Array.from(byStatus.entries())
      .map(([status, statusRecords]) => ({
        status,
        count: statusRecords.length,
        totalAmount: statusRecords.reduce((sum, r) => sum + this.getAmountByPaymentType(r), 0),
        percentage: (statusRecords.length / total) * 100,
        records: statusRecords
      }))
      .sort((a, b) => b.count - a.count);
  });

  // Records by Payment Type
  recordsByPaymentType = computed<RecordsByPaymentType[]>(() => {
    const records = this.allRecords();
    if (records.length === 0) return [];

    const byType = new Map<string, PendingByGapRecord[]>();

    records.forEach(record => {
      const type = record['payment_type_name'] || 'Unknown';
      if (!byType.has(type)) {
        byType.set(type, []);
      }
      byType.get(type)!.push(record);
    });

    const total = records.length;
    return Array.from(byType.entries())
      .map(([paymentType, typeRecords]) => {
        const totalAmount = typeRecords.reduce((sum, r) => sum + this.getAmountByPaymentType(r), 0);
        return {
          paymentType,
          count: typeRecords.length,
          totalAmount,
          avgAmount: totalAmount / typeRecords.length,
          percentage: (typeRecords.length / total) * 100,
          records: typeRecords
        };
      })
      .sort((a, b) => b.count - a.count);
  });

  // Records by Slot
  recordsBySlot = computed<RecordsBySlot[]>(() => {
    const records = this.allRecords();
    if (records.length === 0) return [];

    const bySlot = new Map<string, PendingByGapRecord[]>();

    records.forEach(record => {
      const slot = record['slot_name'] || 'Unknown';
      if (!bySlot.has(slot)) {
        bySlot.set(slot, []);
      }
      bySlot.get(slot)!.push(record);
    });

    const total = records.length;
    return Array.from(bySlot.entries())
      .map(([slot, slotRecords]) => ({
        slot,
        count: slotRecords.length,
        totalAmount: slotRecords.reduce((sum, r) => sum + this.getAmountByPaymentType(r), 0),
        percentage: (slotRecords.length / total) * 100,
        records: slotRecords
      }))
      .sort((a, b) => b.count - a.count);
  });

  // Highest Recurring Amounts (Top 10) - Only for Recurring payment types
  highestRecurringAmounts = computed<RecurringAmountRecord[]>(() => {
    const records = this.allRecords();
    if (records.length === 0) return [];

    return records
      .filter(r => r['payment_type_name'] === 'Recurring' && parseFloat(r['recurring_amount'] || '0') > 0)
      .map(r => ({
        arrangementId: r['arrangement_id'],
        contractCode: r['contract_code'],
        patientName: r['patient_name'],
        recurringAmount: parseFloat(r['recurring_amount'] || '0'),
        scheduledAmount: this.getAmountByPaymentType(r),
        remainingPayments: r['remaining_payments'] || 0,
        createdBy: r['created_by'],
        gapRange: r['gapRange'],
        arrangementDate: r['arrangement_date']
      }))
      .sort((a, b) => b.recurringAmount - a.recurringAmount)
      .slice(0, 10);
  });

  // Lowest Recurring Amounts (Bottom 10) - Only for Recurring payment types
  lowestRecurringAmounts = computed<RecurringAmountRecord[]>(() => {
    const records = this.allRecords();
    if (records.length === 0) return [];

    return records
      .filter(r => r['payment_type_name'] === 'Recurring' && parseFloat(r['recurring_amount'] || '0') > 0)
      .map(r => ({
        arrangementId: r['arrangement_id'],
        contractCode: r['contract_code'],
        patientName: r['patient_name'],
        recurringAmount: parseFloat(r['recurring_amount'] || '0'),
        scheduledAmount: this.getAmountByPaymentType(r),
        remainingPayments: r['remaining_payments'] || 0,
        createdBy: r['created_by'],
        gapRange: r['gapRange'],
        arrangementDate: r['arrangement_date']
      }))
      .sort((a, b) => a.recurringAmount - b.recurringAmount)
      .slice(0, 10);
  });

  // Highest Partial Amounts (Top 10) - Only for Partial payment types
  highestPartialAmounts = computed<PartialAmountRecord[]>(() => {
    const records = this.allRecords();
    if (records.length === 0) return [];

    return records
      .filter(r => r['payment_type_name'] === 'Partial' && parseFloat(r['partial_amount'] || '0') > 0)
      .map(r => ({
        arrangementId: r['arrangement_id'],
        contractCode: r['contract_code'],
        patientName: r['patient_name'],
        partialAmount: parseFloat(r['partial_amount'] || '0'),
        createdBy: r['created_by'],
        gapRange: r['gapRange'],
        arrangementDate: r['arrangement_date'],
        contractStatus: r['contract_status'] || 'Unknown'
      }))
      .sort((a, b) => b.partialAmount - a.partialAmount)
      .slice(0, 10);
  });

  // Lowest Partial Amounts (Bottom 10) - Only for Partial payment types
  lowestPartialAmounts = computed<PartialAmountRecord[]>(() => {
    const records = this.allRecords();
    if (records.length === 0) return [];

    return records
      .filter(r => r['payment_type_name'] === 'Partial' && parseFloat(r['partial_amount'] || '0') > 0)
      .map(r => ({
        arrangementId: r['arrangement_id'],
        contractCode: r['contract_code'],
        patientName: r['patient_name'],
        partialAmount: parseFloat(r['partial_amount'] || '0'),
        createdBy: r['created_by'],
        gapRange: r['gapRange'],
        arrangementDate: r['arrangement_date'],
        contractStatus: r['contract_status'] || 'Unknown'
      }))
      .sort((a, b) => a.partialAmount - b.partialAmount)
      .slice(0, 10);
  });

  // Arrangements by Arrangement Date (Timeline)
  arrangementsByArrangementDate = computed<TimelineRecord[]>(() => {
    const records = this.allRecords();
    if (records.length === 0) return [];

    const byDate = new Map<string, { count: number, totalAmount: number, gapRange: string }>();

    records.forEach(record => {
      const dateStr = record['arrangement_date']?.split('T')[0] || 'Unknown';
      if (!byDate.has(dateStr)) {
        byDate.set(dateStr, { count: 0, totalAmount: 0, gapRange: record['gapRange'] });
      }
      const entry = byDate.get(dateStr)!;
      entry.count++;
      entry.totalAmount += this.getAmountByPaymentType(record);
    });

    return Array.from(byDate.entries())
      .map(([date, data]) => ({
        date,
        dateLabel: new Date(date).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        }),
        count: data.count,
        totalAmount: data.totalAmount,
        gapRange: data.gapRange
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  });

  // Arrangements by Created At (When arrangements were created)
  arrangementsByCreatedAt = computed<RecordsByPeriod[]>(() => {
    const records = this.allRecords().filter(r => r['days_until'] <= 90);
    if (records.length === 0) return [];

    const byMonth = new Map<string, { records: PendingByGapRecord[], totalAmount: number }>();

    records.forEach(record => {
      const createdAt = record['created_at'];
      if (!createdAt) return;
      
      const date = new Date(createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!byMonth.has(monthKey)) {
        byMonth.set(monthKey, { records: [], totalAmount: 0 });
      }
      const entry = byMonth.get(monthKey)!;
      entry.records.push(record);
      entry.totalAmount += this.getAmountByPaymentType(record);
    });

    return Array.from(byMonth.entries())
      .map(([period, data]) => {
        const recurringCount = data.records.filter(r => r['payment_type_name'] === 'Recurring').length;
        const avgDaysUntil = data.records.reduce((sum, r) => sum + (r['days_until'] || 0), 0) / data.records.length;
        const date = new Date(period + '-01');
        return {
          period,
          periodLabel: date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
          count: data.records.length,
          totalAmount: data.totalAmount,
          recurringCount,
          partialCount: data.records.length - recurringCount,
          avgDaysUntil,
          records: data.records
        };
      })
      .sort((a, b) => a.period.localeCompare(b.period));
  });

  // Gap Distribution Analysis
  gapDistribution = computed<GapDistribution[]>(() => {
    const data = this.pendingData();
    if (!data) return [];

    const totalCount = data.summary.total_pending;
    const totalAmount = data.summary.total_scheduled_amount;

    return data.by_gap.map(gap => ({
      gapRange: gap.gap_range,
      gapOrder: gap.gap_order,
      count: gap.total_arrangements,
      percentage: (gap.total_arrangements / totalCount) * 100,
      totalAmount: gap.total_scheduled_amount,
      amountPercentage: (gap.total_scheduled_amount / totalAmount) * 100,
      avgDaysUntil: gap.avg_days_until,
      hasFarFutureAlert: gap.far_future_count > 0
    }));
  });

  // Admin Performance Metrics
  adminPerformanceMetrics = computed<AdminPerformanceMetrics[]>(() => {
    const admins = this.recordsByAdmin();
    if (admins.length === 0) return [];

    const maxArrangements = Math.max(...admins.map(a => a.count));
    const now = new Date();

    return admins.map(admin => {
      const recentRecords = admin.records.filter(r => {
        const createdAt = new Date(r['created_at']);
        const daysDiff = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
        return daysDiff <= 30;
      });

      return {
        adminName: admin.adminName,
        totalArrangements: admin.count,
        avgScheduledAmount: admin.totalScheduledAmount / admin.count,
        recurringPercentage: (admin.recurringCount / admin.count) * 100,
        contractDiversity: admin.contracts.length,
        recentActivityScore: (recentRecords.length / admin.count) * 100
      };
    });
  });

  // Far Future Records (90+ days)
  farFutureRecords = computed<PendingByGapRecord[]>(() => {
    const records = this.allRecords();
    return records.filter(r => r['days_until'] > 90);
  });

  // Records with high remaining payments
  highRemainingPayments = computed<PendingByGapRecord[]>(() => {
    const records = this.allRecords();
    return records
      .filter(r => (r['remaining_payments'] || 0) > 5)
      .sort((a, b) => (b['remaining_payments'] || 0) - (a['remaining_payments'] || 0))
      .slice(0, 20);
  });

  // Update data
  setData(data: PendingByGapAPI | null): void {
    this.pendingData.set(data);
    
    if (data) {
      const allRecords: (PendingByGapRecord & { gapRange: string })[] = [];
      data.by_gap.forEach(gap => {
        if (gap.records) {
          gap.records.forEach(record => {
            allRecords.push({ ...record, gapRange: gap.gap_range });
          });
        }
      });
      this.allRecords.set(allRecords);
    } else {
      this.allRecords.set([]);
    }
  }

  // Get raw data
  getRawData(): PendingByGapAPI | null {
    return this.pendingData();
  }

  // Get all records
  getAllRecords(): (PendingByGapRecord & { gapRange: string })[] {
    return this.allRecords();
  }
}

